(function () {
  'use strict';

  if (window.__udbAovDiscountNoticeLoaded) return;
  window.__udbAovDiscountNoticeLoaded = true;

  const AOV_APPLY_DISCOUNTS_PATTERN = /apply-discounts/i;

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function clearBlockState() {
    window.__udbLastDiscountBlock = null;
  }

  function inspectCartRejection(cart, attemptedCode) {
    return Boolean(
      window.UdbDiscountApplyGuard?.evaluatePostApplyRejection?.(cart, attemptedCode),
    );
  }

  function handleCartDiscountResult(cart) {
    if (!cart) return;
    window.UdbAovDiscountUi?.syncFromCart?.(cart);
  }

  function syncCartLabels(cart) {
    window.UdbAovDiscountUi?.syncFromCart?.(cart);
    window.AovCartLabelSync?.applyCartLabels?.(cart, { force: true });
  }

  function fetchCartAndSync() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then((response) => response.json())
      .then((cart) => {
        clearBlockState();
        syncCartLabels(cart);
        return cart;
      })
      .catch(() => null);
  }

  function getGraphqlBody(body) {
    if (!body) return null;

    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return null;
      }
    }

    return typeof body === 'object' ? body : null;
  }

  function getRequestedDiscountCodesFromBody(body) {
    const parsed = getGraphqlBody(body);
    if (!parsed) return null;

    const vars = parsed.variables || {};
    if (Array.isArray(vars.discountCodes)) return vars.discountCodes;
    if (Array.isArray(vars.input?.discountCodes)) return vars.input.discountCodes;
    return null;
  }

  function isAovDiscountApplyRequest(body) {
    const codes = getRequestedDiscountCodesFromBody(body);
    return Array.isArray(codes) && codes.some((code) => String(code || '').trim());
  }

  function isAovDiscountRemovalRequest(body) {
    const parsed = getGraphqlBody(body);
    if (!parsed) return false;

    const query = String(parsed.query || '');
    const operationName = String(parsed.operationName || '');
    const operationText = `${operationName} ${query}`;
    if (/remove/i.test(operationText) && /discount/i.test(operationText)) {
      return true;
    }

    const codes = getRequestedDiscountCodesFromBody(body);
    return Array.isArray(codes) && codes.length === 0;
  }

  function getAttemptedCodeFromRequest(body) {
    if (isAovDiscountRemovalRequest(body) || !isAovDiscountApplyRequest(body)) {
      return '';
    }

    return (
      window.UdbDiscountApplyGuard?.extractDiscountCodeFromGraphqlBody?.(body) ||
      String(window.__udbLastAttemptedDiscountCode || '').trim()
    );
  }

  function findApplyContextFromEvent(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    let button = null;
    let input = null;

    for (const node of path) {
      if (!node || node.nodeType !== 1) continue;

      if (!button && node.matches?.('button')) {
        const label = String(node.textContent || '').trim();
        if (/apply/i.test(label) || /anwenden/i.test(label)) {
          button = node;
        }
      }

      if (!input && node.matches?.('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])')) {
        const value = String(node.value || '').trim();
        if (value) {
          input = node;
        }
      }
    }

    return { button, input, code: String(input?.value || '').trim() };
  }

  function isAovApplyDiscountsRequest(method, url) {
    return String(method || 'POST').toUpperCase() === 'POST' && AOV_APPLY_DISCOUNTS_PATTERN.test(String(url || ''));
  }

  function handleAovApplyDiscountsResponse(payload, attemptedCode, requestBody) {
    const guard = window.UdbDiscountApplyGuard;
    if (!guard?.parseAovApplyDiscountsResponse || !guard?.resolveAovDiscountApplyOutcome) {
      fetchCartAndSync();
      return;
    }

    const graphqlResult = guard.parseAovApplyDiscountsResponse(payload);
    if (!graphqlResult) {
      fetchCartAndSync();
      return;
    }

    if (isAovDiscountRemovalRequest(requestBody) || !isAovDiscountApplyRequest(requestBody)) {
      window.__udbLastAttemptedDiscountCode = '';
      fetchCartAndSync();
      return;
    }

    const code =
      String(attemptedCode || '').trim() ||
      String(
        graphqlResult.discountCodes?.[graphqlResult.discountCodes.length - 1]?.code || '',
      ).trim() ||
      String(window.__udbLastAttemptedDiscountCode || '').trim();

    if (!code) {
      return;
    }

    window.__udbLastAttemptedDiscountCode = code;

    guard.resolveAovDiscountApplyOutcome(graphqlResult, code).then(() => {
      const fetchCart =
        window.AovCartLabelSync?.fetchCartOnce ||
        (() => fetch('/cart.js').then((r) => r.json()));

      fetchCart()
        .then((cart) => {
          clearBlockState();

          if (window.SbbCouponFlow?.isAwaitingManualDiscount?.()) {
            window.SbbCouponFlow?.completeManualDiscountApply?.(cart);
          }

          syncCartLabels(cart);
        })
        .catch(() => {});
    });
  }

  function dispatchXhrJsonResponse(xhr, payload) {
    const body = JSON.stringify(payload);
    Object.defineProperty(xhr, 'status', { configurable: true, value: 200 });
    Object.defineProperty(xhr, 'responseText', { configurable: true, value: body });
    Object.defineProperty(xhr, 'response', { configurable: true, value: body });
    Object.defineProperty(xhr, 'readyState', { configurable: true, value: 4 });
    xhr.dispatchEvent(new Event('readystatechange'));
    xhr.dispatchEvent(new Event('load'));
    xhr.dispatchEvent(new Event('loadend'));
  }

  async function blockAovApplyDiscountIfNeeded(attemptedCode) {
    const guard = window.UdbDiscountApplyGuard;
    if (!guard?.guardAovApplyDiscountBeforeSend) {
      return null;
    }

    const code = String(attemptedCode || '').trim();
    if (!code) {
      return null;
    }

    const result = await guard.guardAovApplyDiscountBeforeSend(code);
    if (!result?.blocked) {
      return null;
    }

    return result.mockResponse || guard.buildBlockedAovApplyDiscountsGraphqlResponse?.(code);
  }

  function patchFetchForAovApplyDiscounts() {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async function patchedFetchForAovApplyDiscountNotice(input, init) {
      const url = String(typeof input === 'string' ? input : input?.url || '');
      const method = String(init?.method || 'GET');
      const requestBody = init?.body;

      if (isAovApplyDiscountsRequest(method, url)) {
        const shouldGuard = isAovDiscountApplyRequest(requestBody);
        const attemptedCode = getAttemptedCodeFromRequest(requestBody);
        const mockPayload = shouldGuard ? await blockAovApplyDiscountIfNeeded(attemptedCode) : null;

        if (mockPayload) {
          return new Response(JSON.stringify(mockPayload), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      const response = await originalFetch(input, init);

      if (!isAovApplyDiscountsRequest(method, url) || !response.ok) {
        return response;
      }

      try {
        const payload = await response.clone().json();
        handleAovApplyDiscountsResponse(
          payload,
          getAttemptedCodeFromRequest(requestBody),
          requestBody,
        );
      } catch (error) {
        fetchCartAndSync();
      }

      return response;
    };
  }

  function patchXhrForAovApplyDiscounts() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function open(method, url, ...args) {
      this.__udbAovApplyDiscountMethod = method;
      this.__udbAovApplyDiscountUrl = url;
      return originalOpen.call(this, method, url, ...args);
    };

    XMLHttpRequest.prototype.send = function send(body, ...args) {
      const xhr = this;
      const method = xhr.__udbAovApplyDiscountMethod;
      const url = xhr.__udbAovApplyDiscountUrl;

      if (isAovApplyDiscountsRequest(method, url)) {
        const shouldGuard = isAovDiscountApplyRequest(body);
        const attemptedCode = getAttemptedCodeFromRequest(body);

        blockAovApplyDiscountIfNeeded(shouldGuard ? attemptedCode : '').then((mockPayload) => {
          if (mockPayload) {
            dispatchXhrJsonResponse(xhr, mockPayload);
            return;
          }

          xhr.addEventListener(
            'load',
            function onAovApplyDiscountLoad() {
              if (xhr.status < 200 || xhr.status >= 300) {
                return;
              }

              try {
                const payload = JSON.parse(xhr.responseText || '{}');
                handleAovApplyDiscountsResponse(
                  payload,
                  getAttemptedCodeFromRequest(body),
                  body,
                );
              } catch (error) {
                fetchCartAndSync();
              }
            },
            { once: true },
          );

          originalSend.call(xhr, body, ...args);
        });

        return;
      }

      return originalSend.call(xhr, body, ...args);
    };
  }

  function bindApplyCapture() {
    document.addEventListener(
      'click',
      (event) => {
        if (window.__udbAllowNextDiscountApply) {
          window.__udbAllowNextDiscountApply = false;
          return;
        }

        const { button, code } = findApplyContextFromEvent(event);
        if (!button || !code) return;

        const guard = window.UdbDiscountApplyGuard;
        if (!guard?.evaluatePreApplyDiscount) {
          window.__udbLastAttemptedDiscountCode = code;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        guard.evaluatePreApplyDiscount(code).then((decision) => {
          if (decision.allow) {
            window.__udbLastAttemptedDiscountCode = code;
            window.__udbAllowNextDiscountApply = true;
            button.click();
          }
        });
      },
      true,
    );
  }

  function init() {
    patchFetchForAovApplyDiscounts();
    patchXhrForAovApplyDiscounts();
    bindApplyCapture();
    window.UdbAovDiscountUi?.init?.();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.UdbAovDiscountNotice = {
    inspectCartRejection,
    handleCartDiscountResult,
    handleAovApplyDiscountsResponse,
  };
})();

(function () {
  'use strict';

  if (window.__udbAovDiscountUiLoaded) return;
  window.__udbAovDiscountUiLoaded = true;

  const DISCOUNT_ROW_SELECTOR = '[class*="AOV-CDMinimal__DiscountSection__List"] > *';

  let drawerWatchObserver = null;
  let lastSyncedCode = null;

  function walkDeepElements(root, visit) {
    const start = root || document.body;
    if (!start) return;

    const stack = [start];
    const seen = new Set();

    while (stack.length) {
      const node = stack.pop();
      if (!node || node.nodeType !== 1 || seen.has(node)) continue;
      seen.add(node);

      visit(node);

      if (node.shadowRoot) {
        walkDeepElements(node.shadowRoot, visit);
        continue;
      }

      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]);
      }
    }
  }

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function rowMatchesUdbCode(node, code) {
    const normalized = normalizeCode(code);
    if (!normalized) return false;
    return normalizeCode(node.textContent || '').includes(normalized);
  }

  function hideDiscountRow(node) {
    if (!node || node.dataset.udbNonTierSuppressed === 'true') return;
    node.dataset.udbNonTierSuppressed = 'true';
    node.dataset.udbNonTierPrevDisplay = node.style.display || '';
    node.style.setProperty('display', 'none', 'important');
  }

  function showDiscountRow(node) {
    if (!node || node.dataset.udbNonTierSuppressed !== 'true') return;
    const previous = node.dataset.udbNonTierPrevDisplay;
    if (previous) {
      node.style.display = previous;
    } else {
      node.style.removeProperty('display');
    }
    delete node.dataset.udbNonTierSuppressed;
    delete node.dataset.udbNonTierPrevDisplay;
  }

  function releaseNonUdbDiscountRows(root) {
    walkDeepElements(root, (node) => {
      if (node.dataset?.udbNonTierSuppressed === 'true') {
        showDiscountRow(node);
      }
    });
  }

  function suppressNonUdbDiscountRows(root, code) {
    const normalized = normalizeCode(code);
    if (!normalized) return;

    walkDeepElements(root, (node) => {
      if (!node.matches?.(DISCOUNT_ROW_SELECTOR)) return;
      if (rowMatchesUdbCode(node, normalized)) {
        showDiscountRow(node);
        return;
      }
      hideDiscountRow(node);
    });
  }

  function syncFromCart(cart) {
    const state = window.UdbDiscountApplyGuard?.evaluateUdbTierWin?.(cart);
    const drawer = document.getElementById('aov-cart-drawer');
    if (!drawer) return;

    if (!state?.isActive) {
      lastSyncedCode = null;
      releaseNonUdbDiscountRows(drawer);
      return;
    }

    lastSyncedCode = state.code;
    suppressNonUdbDiscountRows(drawer, state.code);
  }

  function watchDrawer() {
    const drawer = document.getElementById('aov-cart-drawer');
    if (!drawer || drawer.dataset.udbDiscountUiWatch) return;

    drawer.dataset.udbDiscountUiWatch = 'true';
    drawerWatchObserver = new MutationObserver(() => {
      if (!lastSyncedCode) return;
      suppressNonUdbDiscountRows(drawer, lastSyncedCode);
    });
    drawerWatchObserver.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    watchDrawer();

    document.addEventListener('cart:change', (event) => {
      const cart = event?.detail?.cart;
      if (cart) {
        syncFromCart(cart);
      }
    });
  }

  window.UdbAovDiscountUi = {
    init,
    syncFromCart,
    suppressNonUdbDiscountRows,
    releaseNonUdbDiscountRows,
  };
})();
